import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useReservations = (propertyId: string | undefined, opts?: { live?: boolean }) => {
  return useQuery({
    queryKey: ["reservations", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];

      // Include recent past stays so turnover windows (checkout → next check-in) resolve.
      const past = new Date();
      past.setDate(past.getDate() - 90);
      const pastStr = past.toISOString().split("T")[0];

      const { data, error } = await supabase
        .from("reservations")
        .select("*")
        .eq("property_id", propertyId)
        .gte("start_date", pastStr)
        .order("start_date", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!propertyId,
    // Schedule needs guest-left / waiting updates from poll + sync without a hard refresh.
    staleTime: opts?.live ? 15_000 : 1000 * 60 * 2,
    refetchInterval: opts?.live ? 45_000 : false,
    refetchOnMount: opts?.live ? "always" : false,
    refetchOnWindowFocus: opts?.live ? true : false,
    placeholderData: [],
  });
};

/** Remove a reservation permanently. Suppresses its external_id so Hospitable sync will not re-import it, and deletes open linked turnover tasks. */
export const useDeleteReservation = (propertyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reservationId: string) => {
      const { data, error } = await supabase.rpc("delete_reservation_and_suppress", {
        p_reservation_id: reservationId,
      });
      if (error) {
        const msg = error.message || "Failed to remove reservation";
        if (/full access|only full access|not allowed/i.test(msg)) {
          throw new Error(
            "Only Full Access team members can remove reservations. Ask the account owner if this needs to be removed.",
          );
        }
        throw new Error(msg);
      }
      return data as { ok?: boolean; tasks_deleted?: number } | null;
    },
    onSuccess: () => {
      if (!propertyId) return;
      queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] });
    },
  });
};
